import * as React from "react";

import { useFieldFormContext, ErrorMessage } from "../../common";

type CardType = "amex" | "other";

const detectCardType = (digits: string): CardType => {
    if (/^3[47]/.test(digits)) return "amex";
    return "other";
};

const formatCardNumber = (digits: string, type: CardType): string => {
    if (type === "amex") {
        // 4-6-5 grouping
        return [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)]
            .filter(s => s.length > 0)
            .join(" ");
    }
    // 4-4-4-4 grouping
    return (digits.match(/.{1,4}/g) ?? []).join(" ");
};

export const PaymentCreditCard = () => {
    const { registerField, register, setValue, unregister, isInvalid } = useFieldFormContext();
    const [cardDisplay, setCardDisplay] = React.useState("");

    React.useEffect(() => {
        return () => { unregister("payment.creditCard.cardNumber"); };
    }, []);

    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, "");
        const type = detectCardType(digits);
        const maxDigits = type === "amex" ? 15 : 16;
        const trimmed = digits.slice(0, maxDigits);
        setCardDisplay(formatCardNumber(trimmed, type));
        setValue("payment.creditCard.cardNumber", trimmed, { shouldValidate: true });
    };

    const fields = {
        cardNumber: {
            id: "cardNumber",
            name: "payment.creditCard.cardNumber",
            options: {
                required: { value: true, message: "Enter a valid credit card number" },
                maxLength: 25,
                minLength: 12
            }
        },
        securityCode: {
            id: "securityCode",
            name: "payment.creditCard.securityCode",
            options: {
                required: { value: true, message: "Enter a security code" },
                maxLength: 4,
                minLength: 3
            }
        },
        expirationMonth: {
            id: "expirationMonth",
            name: "payment.creditCard.expirationMonth",
            options: {
                required: { value: true, message: "Enter an expiration month" },
            }
        },
        expirationYear: {
            id: "expirationYear",
            name: "payment.creditCard.expirationYear",
            options: {
                required: { value: true, message: "Enter an expiration year" }
            },
        }
    }

    // Generate year options for expiration year select starting from the current year
    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 12 }, (_, i) => {
            const year = currentYear + i;
            return <option key={year} value={year}>{year}</option>;
        });

        return years;
    }

    register(fields.cardNumber.name, fields.cardNumber.options);

    return (
        <>
            <div className="form-group card-number">
                <label htmlFor={fields.cardNumber.id}>Credit Card Number</label>
                <input
                    type="text"
                    id={fields.cardNumber.id}
                    value={cardDisplay}
                    onChange={handleCardNumberChange}
                    aria-invalid={isInvalid(fields.cardNumber.name)}
                    maxLength={19}
                    autoComplete="cc-number"
                />
                <ErrorMessage name={fields.cardNumber.name}/>
            </div>
            <div className="form-group security-code">
                <label htmlFor={fields.securityCode.id}>Security Code</label>
                <input type="text" {...registerField(fields.securityCode)} />
                <ErrorMessage name={fields.securityCode.name} />
            </div>
            <div className="form-group expiration-month">
                <label htmlFor={fields.expirationMonth.id}>Expiration Month</label>
                <select {...registerField(fields.expirationMonth)}>
                    <option value=""></option>
                    <option value="01">01</option>
                    <option value="02">02</option>
                    <option value="03">03</option>
                    <option value="04">04</option>
                    <option value="05">05</option>
                    <option value="06">06</option>
                    <option value="07">07</option>
                    <option value="08">08</option>
                    <option value="09">09</option>
                    <option value="10">10</option>
                    <option value="11">11</option>
                    <option value="12">12</option>
                </select>
                <ErrorMessage name={fields.expirationMonth.name} />
            </div>
            <div className="form-group expiration-year">
                <label htmlFor={fields.expirationYear.id}>Expiration Year</label>
                <select {...registerField(fields.expirationYear)}>
                    <option value=""></option>
                    {getYearOptions()}
                </select>
                <ErrorMessage name={fields.expirationYear.name} />
            </div>
        </>
    );
}